import type {
  AppEvent, IntervalState, PlanRecord, PlanStateRecord, PlanType,
  QuotaState, WindowState,
} from '../core/types.js';
import { uuid } from '../core/ids.js';
import { formatDuration, formatHm, localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { habits, logEvent, metaSet, planStates, plans } from '../storage/db.js';
import { getTemplate } from '../domain/templates.js';

export type SuggestionKind =
  | 'increase_challenge'
  | 'decrease_challenge'
  | 'hold_steady'
  | 'switch_strategy';

export interface PlanSuggestion {
  id: string;
  kind: SuggestionKind;
  headline: string;
  evidence: string;
  /** When kind=increase/decrease, calling this mutates the plan target and persists. */
  apply?: () => Promise<void>;
}

export interface AdaptationInput {
  plan: PlanRecord;
  planState: PlanStateRecord;
  /** Recent events for the habit, any order. */
  events: AppEvent[];
  /** Reference time, defaults to now. */
  nowMs?: number;
  /**
   * Self-reported confidence at plan start (1-5). When low (<=2), increase
   * thresholds are tightened (4 wins required → 5) and decreases trigger
   * sooner (3 misses → 2), biasing the engine toward gentler progression.
   */
  confidence?: 1 | 2 | 3 | 4 | 5 | undefined;
}

/**
 * Build a single plan suggestion for the active week. Returns null when there
 * isn't enough signal yet, or when a paused/graduated plan is the input.
 */
export function suggestPlanAdjustment(input: AdaptationInput): PlanSuggestion | null {
  const { plan, planState, events } = input;
  const nowMs = input.nowMs ?? Date.now();
  if (plan.lifecycleState === 'paused' || plan.lifecycleState === 'graduated' || plan.lifecycleState === 'archived') {
    return null;
  }
  const weekAgo = nowMs - 7 * 86400000;
  const inWindow = events.filter(
    (e) => e.status !== 'undone' && new Date(e.occurredAt).getTime() >= weekAgo,
  );
  if (inWindow.length < 3) return null;

  const lowConfidence = input.confidence !== undefined && input.confidence <= 2;
  switch (planState.state.planType) {
    case 'interval': return suggestInterval(plan, planState, planState.state, inWindow, lowConfidence);
    case 'quota': return suggestQuota(plan, planState, planState.state, inWindow, lowConfidence);
    case 'window': return suggestWindow(plan, planState, planState.state, inWindow, lowConfidence);
    default: return null;
  }
}

function suggestInterval(
  plan: PlanRecord,
  ps: PlanStateRecord,
  state: IntervalState,
  events: AppEvent[],
  lowConfidence: boolean,
): PlanSuggestion | null {
  const winsNeeded = lowConfidence ? 5 : 4;
  const missesNeeded = lowConfidence ? 2 : 3;
  const usage = events.filter((e) => e.type === 'usage_logged');
  if (usage.length === 0) return { id: `${plan.id}.hold`, kind: 'hold_steady',
    headline: 'No use logged this week.',
    evidence: 'Nothing to tune — keep using urge support when needed.' };

  // Count near_miss / early_slip outcomes from payload, fall back to wait ratio.
  let stretches = 0; let successes = 0; let nearMiss = 0; let earlySlip = 0;
  for (const e of usage) {
    const payload = e.payload as { outcome?: string; effectiveStartAt?: string };
    if (payload.outcome === 'stretch_success') stretches++;
    else if (payload.outcome === 'success') successes++;
    else if (payload.outcome === 'near_miss') nearMiss++;
    else if (payload.outcome === 'early_slip') earlySlip++;
  }
  const wins = stretches + successes;
  const misses = earlySlip;
  const target = state.targetIntervalMs;
  const minMs = state.minIntervalMs;
  const maxMs = state.maxIntervalMs;

  if (wins >= winsNeeded && earlySlip === 0 && nearMiss <= 1) {
    const next = Math.min(maxMs, Math.round(target * 1.2));
    if (next > target) {
      return {
        id: `${plan.id}.up`,
        kind: 'increase_challenge',
        headline: `You handled ${formatDuration(target)}. Try ${formatDuration(next)}?`,
        evidence: `${wins} wins, 0 early slips this week.`,
        apply: async () => {
          const updated: PlanStateRecord = {
            ...ps,
            state: { ...state, targetIntervalMs: next, baselineIntervalMs: Math.max(state.baselineIntervalMs, target) },
            updatedAt: nowIso(),
          };
          await planStates.put(updated);
          await logEvent({
            type: 'plan_adjusted', habitId: plan.habitId, planId: plan.id,
            occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
            payload: { planId: plan.id, kind: 'increase_challenge', from: target, to: next },
          });
        },
      };
    }
  }
  if (misses >= missesNeeded && wins <= 1) {
    const next = Math.max(minMs, Math.round(target * 0.8));
    if (next < target) {
      return {
        id: `${plan.id}.down`,
        kind: 'decrease_challenge',
        headline: `This step may be too much right now. Try ${formatDuration(next)}?`,
        evidence: `${misses} early slips this week. A gentler step rebuilds momentum.`,
        apply: async () => {
          const updated: PlanStateRecord = {
            ...ps,
            state: { ...state, targetIntervalMs: next },
            updatedAt: nowIso(),
          };
          await planStates.put(updated);
          await logEvent({
            type: 'plan_adjusted', habitId: plan.habitId, planId: plan.id,
            occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
            payload: { planId: plan.id, kind: 'decrease_challenge', from: target, to: next },
          });
        },
      };
    }
  }

  // Strategy switch: many uses clustered into a narrow daily window → suggest window plan.
  if (usage.length >= 6) {
    const hours = usage.map((e) => new Date(e.occurredAt).getHours());
    const clustered = clusteredWithin(hours, 4);
    if (clustered) {
      return {
        id: `${plan.id}.switch`,
        kind: 'switch_strategy',
        headline: 'Uses cluster in a narrow time window.',
        evidence: 'A window plan ("hold off until X") may fit better than an interval gap.',
        apply: () => switchPlanStrategy(plan, 'window'),
      };
    }
  }
  return { id: `${plan.id}.hold`, kind: 'hold_steady',
    headline: 'Holding steady this week.',
    evidence: `${wins} wins, ${misses} early slips. Wait for a clearer signal before changing.` };
}

function clusteredWithin(hours: number[], rangeHours: number): boolean {
  if (hours.length === 0) return false;
  const sorted = [...hours].sort((a, b) => a - b);
  const min = sorted[0]!;
  const max = sorted[sorted.length - 1]!;
  return max - min <= rangeHours;
}

function suggestQuota(
  plan: PlanRecord,
  ps: PlanStateRecord,
  state: QuotaState,
  events: AppEvent[],
  lowConfidence: boolean,
): PlanSuggestion | null {
  const successNeeded = lowConfidence ? 3 : 2;
  const usage = events.filter((e) => e.type === 'usage_logged').length;
  const period = state.period;
  const cap = state.currentQuota;
  const used = state.usedThisPeriod;
  if (state.successPeriods >= successNeeded && cap > 1) {
    const next = Math.max(1, cap - state.reductionStep);
    const word = successNeeded === 2 ? 'Two' : successNeeded === 3 ? 'Three' : `${successNeeded}`;
    return {
      id: `${plan.id}.up`,
      kind: 'increase_challenge',
      headline: `${word} ${period}s under cap. Lower to ${next}?`,
      evidence: `Current cap ${cap}, used ${used}. A smaller cap keeps momentum.`,
      apply: async () => {
        const updated: PlanStateRecord = {
          ...ps,
          state: { ...state, currentQuota: next },
          updatedAt: nowIso(),
        };
        await planStates.put(updated);
        await logEvent({
          type: 'plan_adjusted', habitId: plan.habitId, planId: plan.id,
          occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
          payload: { planId: plan.id, kind: 'increase_challenge', from: cap, to: next },
        });
      },
    };
  }
  if (used > cap && usage >= cap) {
    const next = Math.min(state.baselineQuota, cap + 1);
    return {
      id: `${plan.id}.down`,
      kind: 'decrease_challenge',
      headline: `Over the cap this ${period}. Try ${next} this period?`,
      evidence: `Used ${used} of ${cap}. A gentler cap is still reduction.`,
      apply: async () => {
        const updated: PlanStateRecord = {
          ...ps,
          state: { ...state, currentQuota: next },
          updatedAt: nowIso(),
        };
        await planStates.put(updated);
        await logEvent({
          type: 'plan_adjusted', habitId: plan.habitId, planId: plan.id,
          occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
          payload: { planId: plan.id, kind: 'decrease_challenge', from: cap, to: next },
        });
      },
    };
  }
  return { id: `${plan.id}.hold`, kind: 'hold_steady',
    headline: 'Holding the cap.',
    evidence: `${used} of ${cap} used. Watch one more period before adjusting.` };
}

function suggestWindow(
  plan: PlanRecord,
  ps: PlanStateRecord,
  state: WindowState,
  _events: AppEvent[],
  lowConfidence: boolean,
): PlanSuggestion | null {
  const successNeeded = lowConfidence ? 5 : 4;
  const missNeeded = lowConfidence ? 3 : 4;
  if (state.successDays >= successNeeded && state.missDays <= 1) {
    const next = Math.min(state.targetFirstUseMinutes + state.shiftStepMinutes, 23 * 60);
    if (next > state.targetFirstUseMinutes) {
      return {
        id: `${plan.id}.up`,
        kind: 'increase_challenge',
        headline: `Hold until ${formatHm(next)} next week?`,
        evidence: `${state.successDays} success days, ${state.missDays} miss days. Shifting the window builds tolerance.`,
        apply: async () => {
          const updated: PlanStateRecord = {
            ...ps,
            state: { ...state, targetFirstUseMinutes: next },
            updatedAt: nowIso(),
          };
          await planStates.put(updated);
          await logEvent({
            type: 'plan_adjusted', habitId: plan.habitId, planId: plan.id,
            occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
            payload: { planId: plan.id, kind: 'increase_challenge', from: state.targetFirstUseMinutes, to: next },
          });
        },
      };
    }
  }
  if (state.missDays >= missNeeded) {
    const next = Math.max(state.baselineFirstUseMinutes, state.targetFirstUseMinutes - state.shiftStepMinutes);
    if (next < state.targetFirstUseMinutes) {
      return {
        id: `${plan.id}.down`,
        kind: 'decrease_challenge',
        headline: `Pull the window back to ${formatHm(next)}?`,
        evidence: `${state.missDays} miss days. A gentler start beats no start.`,
        apply: async () => {
          const updated: PlanStateRecord = {
            ...ps,
            state: { ...state, targetFirstUseMinutes: next },
            updatedAt: nowIso(),
          };
          await planStates.put(updated);
          await logEvent({
            type: 'plan_adjusted', habitId: plan.habitId, planId: plan.id,
            occurredAt: nowIso(), localDate: localDate(), timezoneOffsetMinutes: tzOffsetMinutes(),
            payload: { planId: plan.id, kind: 'decrease_challenge', from: state.targetFirstUseMinutes, to: next },
          });
        },
      };
    }
  }
  return { id: `${plan.id}.hold`, kind: 'hold_steady',
    headline: 'Window holding steady.',
    evidence: `${state.successDays} success days, ${state.missDays} miss days.` };
}

/**
 * Archive the current plan and seed a new one with the suggested plan type.
 * Uses the habit's domain template for sensible first-week defaults.
 */
/**
 * Promote a calibration plan to active and set a sensible first-week target
 * based on observed usage events. Picks median inter-use gap for interval
 * plans, or the rounded weekly count for quota plans.
 */
export async function promoteCalibrationPlan(
  plan: PlanRecord,
  planState: PlanStateRecord,
  events: AppEvent[],
): Promise<void> {
  const uses = events
    .filter((e) => e.type === 'usage_logged' && e.status !== 'undone')
    .slice()
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
  if (planState.state.planType === 'interval' && uses.length >= 2) {
    const gaps: number[] = [];
    for (let i = 1; i < uses.length; i++) {
      gaps.push(new Date(uses[i]!.occurredAt).getTime() - new Date(uses[i - 1]!.occurredAt).getTime());
    }
    gaps.sort((a, b) => a - b);
    let median: number;
    if (gaps.length === 0) {
      median = planState.state.targetIntervalMs;
    } else if (gaps.length % 2 === 0) {
      const mid = gaps.length / 2;
      median = Math.round(((gaps[mid - 1] ?? 0) + (gaps[mid] ?? 0)) / 2);
    } else {
      median = gaps[Math.floor(gaps.length / 2)] ?? planState.state.targetIntervalMs;
    }
    const target = Math.min(
      planState.state.maxIntervalMs,
      Math.max(planState.state.minIntervalMs, Math.round(median * 1.1)),
    );
    const newState: PlanStateRecord = {
      ...planState,
      state: { ...planState.state, targetIntervalMs: target, baselineIntervalMs: median },
      updatedAt: nowIso(),
    };
    await planStates.put(newState);
  }
  const updated: PlanRecord = {
    ...plan,
    lifecycleState: 'active',
    origin: plan.origin === 'calibration' ? 'quick_start' : plan.origin,
    updatedAt: nowIso(),
  };
  await plans.put(updated);
  await logEvent({
    type: 'plan_adjusted',
    habitId: plan.habitId,
    planId: plan.id,
    occurredAt: nowIso(),
    localDate: localDate(),
    timezoneOffsetMinutes: tzOffsetMinutes(),
    payload: { planId: plan.id, kind: 'calibration_complete', usagesObserved: uses.length },
  });
}

export async function switchPlanStrategy(plan: PlanRecord, to: PlanType): Promise<void> {
  const habit = await habits.get(plan.habitId);
  if (!habit) return;
  const template = getTemplate(habit.category);
  const target = template?.firstWeekTargets[to]?.[1]
    ?? template?.firstWeekTargets[to]?.[0];

  // Archive the old plan.
  const archived: PlanRecord = {
    ...plan,
    active: false,
    lifecycleState: 'archived',
    updatedAt: nowIso(),
  };
  await plans.put(archived);
  await logEvent({
    type: 'plan_archived',
    habitId: plan.habitId,
    planId: plan.id,
    occurredAt: nowIso(),
    localDate: localDate(),
    timezoneOffsetMinutes: tzOffsetMinutes(),
    payload: { planId: plan.id, reason: 'switch_strategy', to },
  });

  // Build new plan + state from the template target.
  const newPlanId = uuid();
  const built = buildSwitchedPlan(to, target);
  const newPlan: PlanRecord = {
    id: newPlanId,
    habitId: plan.habitId,
    schemaVersion: plan.schemaVersion,
    planType: to,
    pace: plan.pace,
    slipStyle: plan.slipStyle,
    goalStyle: plan.goalStyle,
    config: built.config,
    active: true,
    origin: 'template',
    lifecycleState: 'active',
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
  await plans.put(newPlan);
  const newState: PlanStateRecord = {
    planId: newPlanId,
    habitId: plan.habitId,
    schemaVersion: plan.schemaVersion,
    state: built.state,
    updatedAt: nowIso(),
  };
  await planStates.put(newState);
  await metaSet('active_plan_id', newPlanId);

  await logEvent({
    type: 'plan_adjusted',
    habitId: plan.habitId,
    planId: newPlanId,
    occurredAt: nowIso(),
    localDate: localDate(),
    timezoneOffsetMinutes: tzOffsetMinutes(),
    payload: {
      planId: newPlanId,
      kind: 'switch_strategy',
      from: plan.planType,
      to,
      previousPlanId: plan.id,
    },
  });
}

interface BuiltSwitchedPlan {
  config: PlanRecord['config'];
  state: PlanStateRecord['state'];
}

function buildSwitchedPlan(to: PlanType, target: import('../domain/templates.js').FirstWeekTarget | undefined): BuiltSwitchedPlan {
  if (to === 'interval') {
    const startMin = target?.intervalMinutes ?? 5;
    const cfg = {
      baselineIntervalMs: Math.max(1, Math.round(startMin * 0.6)) * 60_000,
      startingIntervalMs: startMin * 60_000,
      minIntervalMs: 60_000,
      maxIntervalMs: 12 * 60 * 60_000,
    };
    return {
      config: cfg,
      state: {
        planType: 'interval',
        targetIntervalMs: cfg.startingIntervalMs,
        baselineIntervalMs: cfg.baselineIntervalMs,
        minIntervalMs: cfg.minIntervalMs,
        maxIntervalMs: cfg.maxIntervalMs,
        successStreak: 0, slipStreak: 0, momentum: 50,
      },
    };
  }
  if (to === 'window') {
    const firstUse = target?.windowFirstUseMinutes ?? 12 * 60;
    const cfg = {
      windowName: 'Daily',
      activeDays: [0, 1, 2, 3, 4, 5, 6],
      baselineFirstUseMinutes: Math.max(0, firstUse - 60),
      startingFirstUseMinutes: firstUse,
      shiftStepMinutes: 15,
    };
    return {
      config: cfg,
      state: {
        planType: 'window',
        windowName: cfg.windowName,
        activeDays: cfg.activeDays,
        baselineFirstUseMinutes: cfg.baselineFirstUseMinutes,
        targetFirstUseMinutes: cfg.startingFirstUseMinutes,
        shiftStepMinutes: cfg.shiftStepMinutes,
        successDays: 0, missDays: 0,
      },
    };
  }
  const reduction = target?.quotaPercentReduction ?? 15;
  const baseline = 14;
  const start = Math.max(1, Math.round(baseline * (1 - reduction / 100)));
  const cfg = { period: 'weekly' as const, baselineQuota: baseline, startingQuota: start, reductionStep: 1 };
  const now = new Date();
  return {
    config: cfg,
    state: {
      planType: 'quota',
      period: 'weekly',
      baselineQuota: baseline,
      currentQuota: start,
      periodStartedAt: now.toISOString(),
      periodEndsAt: new Date(now.getTime() + 7 * 86400000).toISOString(),
      usedThisPeriod: 0, reductionStep: 1, successPeriods: 0,
    },
  };
}

// Re-export needed for tests that may stub plan adjustments.
export const _planAdjustmentInternals = { plans };
